import React from 'react';
import CashFlowChart from './charts/CashFlowChart';
import TransactionPieChart from './charts/TransactionPieChart';
import XIRRComparisonChart from './charts/XIRRComparisonChart';

/**
 * AnalyticsDashboard - Premium analytics dashboard with interactive charts
 */
const AnalyticsDashboard = ({
    purchaseTransactions,
    saleTransactions,
    additionalTransactions,
    overallResults
}) => {
    // Calculate key metrics
    const calculateMetrics = () => {
        const totalInvestment = purchaseTransactions?.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0) || 0;
        const totalReturns = saleTransactions?.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0) || 0;
        const totalCosts = additionalTransactions?.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0) || 0;
        const netProfit = totalReturns - totalInvestment - totalCosts;
        const roi = totalInvestment > 0 ? ((netProfit / totalInvestment) * 100) : 0;

        return {
            totalInvestment,
            totalReturns,
            totalCosts,
            netProfit,
            roi
        };
    };

    const metrics = calculateMetrics();

    const formatCurrency = (value) => {
        return `₹${Math.abs(value).toLocaleString('en-IN')}`;
    };

    return (
        <div style={{ padding: '20px' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <h2 style={{ margin: '0 0 8px 0', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    📊 Advanced Analytics
                    <span style={{
                        fontSize: '0.7rem',
                        padding: '4px 8px',
                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                        color: '#10b981',
                        borderRadius: '12px',
                        fontWeight: 'bold'
                    }}>
                        PREMIUM
                    </span>
                </h2>
                <p style={{ margin: 0, color: '#666', fontSize: '0.95rem' }}>
                    Comprehensive insights into your property performance
                </p>
            </div>

            {/* Key Metrics Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '32px'
            }}>
                <div style={{
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderLeft: '4px solid #667eea'
                }}>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
                        Total Investment
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1a1a2e' }}>
                        {formatCurrency(metrics.totalInvestment)}
                    </div>
                </div>

                <div style={{
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderLeft: '4px solid #10b981'
                }}>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
                        Total Returns
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1a1a2e' }}>
                        {formatCurrency(metrics.totalReturns)}
                    </div>
                </div>

                <div style={{
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderLeft: `4px solid ${metrics.netProfit >= 0 ? '#10b981' : '#ef4444'}`
                }}>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
                        Net Profit
                    </div>
                    <div style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: metrics.netProfit >= 0 ? '#10b981' : '#ef4444'
                    }}>
                        {metrics.netProfit >= 0 ? '+' : '-'}{formatCurrency(metrics.netProfit)}
                    </div>
                </div>

                <div style={{
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderLeft: '4px solid #764ba2'
                }}>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
                        ROI
                    </div>
                    <div style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: metrics.roi >= 0 ? '#10b981' : '#ef4444'
                    }}>
                        {metrics.roi >= 0 ? '+' : ''}{metrics.roi.toFixed(2)}%
                    </div>
                </div>
            </div>

            {/* Charts Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
                gap: '24px'
            }}>
                {/* Cash Flow Chart */}
                <div style={{
                    padding: '24px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                    <CashFlowChart
                        purchaseTransactions={purchaseTransactions}
                        saleTransactions={saleTransactions}
                        additionalTransactions={additionalTransactions}
                    />
                </div>

                {/* Transaction Pie Chart */}
                <div style={{
                    padding: '24px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                    <TransactionPieChart
                        purchaseTransactions={purchaseTransactions}
                        saleTransactions={saleTransactions}
                        additionalTransactions={additionalTransactions}
                    />
                </div>

                {/* XIRR Comparison Chart */}
                <div style={{
                    padding: '24px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    gridColumn: 'span 2'
                }}>
                    <XIRRComparisonChart overallResults={overallResults} />
                </div>
            </div>

            {/* Footer Note */}
            <div style={{
                marginTop: '32px',
                padding: '16px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                fontSize: '0.9rem',
                color: '#666',
                textAlign: 'center'
            }}>
                💡 <strong>Tip:</strong> Hover over charts for detailed information. All data updates automatically when you modify transactions.
            </div>
        </div>
    );
};

export default AnalyticsDashboard;
